import { useState } from 'react';
import { usePreservedCallback } from '@toss/react';
import { useTranslation } from 'next-i18next';

import { logger } from '@zep/core-logger';

import {
  type AchievementStandardWithOrigin,
  type AttachmentState,
} from '@/shared/features/ai-quiz-generation/types';
import { alertDialog } from '@/ui_v3';
import {
  MAX_SELECTION,
  openAchievementStandardSearchModal,
} from '@/views/build/features/achievement-standard-picker';
import { getPromptData } from '@/views/build/features/ai-quiz-generation/utils/getAiEventData';

import { useBuildAiCategory } from '../context/BuildAiCategoryProvider';

/**
 * 성취기준 추가 상태 관리 hook
 *
 * BuildAiCategoryProvider context에서 카테고리 정보를 가져옵니다.
 */
export const useStandardRecommendation = () => {
  const { t } = useTranslation(['build']);
  const { subjectTree, initialSchoolInfoId, initialSubjectId, schoolInfoTree } =
    useBuildAiCategory();

  const [selectedAchievementStandards, setSelectedAchievementStandards] =
    useState<AchievementStandardWithOrigin[]>([]);

  const handleAdd = usePreservedCallback(
    async ({
      attachmentState,
      textPrompt,
    }: {
      attachmentState: AttachmentState;
      textPrompt: string;
    }) => {
      if (selectedAchievementStandards.length >= MAX_SELECTION) {
        alertDialog.open({
          header: {
            title: t('build:build.standard.modal.maxSelection', {
              max: MAX_SELECTION,
            }),
          },
        });
        return;
      }
      logger.track('click_ai_std', getPromptData(attachmentState, textPrompt));

      // 새 성취기준 검색 모달 열기 (외부에서 전달받은 categoryContext 사용)
      const selected = await openAchievementStandardSearchModal({
        categoryContext: {
          schoolInfoTree,
          subjectTree,
          initialSchoolInfoId,
          initialSubjectId,
        },
        textPrompt,
        alreadySelectedStandards: selectedAchievementStandards,
      });

      setSelectedAchievementStandards(selected);
    },
  );

  const handleRemove = usePreservedCallback(
    (achievementStandard: AchievementStandardWithOrigin) => {
      setSelectedAchievementStandards(prev =>
        prev.filter(s => s.code !== achievementStandard.code),
      );
    },
  );

  return {
    selectedAchievementStandards,
    handleAdd,
    handleRemove,
  };
};
